import { API, graphqlOperation } from "aws-amplify";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { ConversationsByProjectIdAndCreatedAtQuery } from "../API";
import { conversationsByProjectIdAndCreatedAt } from "../graphql/queries";
import { useInfiniteQuery } from "@tanstack/react-query";

interface ConversationsPage {
  conversations: any[]; 
  nextToken: string | null;
}

async function fetchConversations({
  projectId,
  limit = 25,
  nextToken = null,
}: {
  projectId: string;
  limit?: number;
  nextToken?: string | null;
}): Promise<ConversationsPage> {
  const query: any = {
    projectId: projectId,
    sortDirection: "DESC",
    filter: {
      status: { eq: "active" },
    },
    limit: limit, // Use the exact limit passed in
    nextToken: nextToken,
  };

  const result = (await API.graphql(
    graphqlOperation(conversationsByProjectIdAndCreatedAt, query)
  )) as GraphQLResult<ConversationsByProjectIdAndCreatedAtQuery>;

  const conversations = result.data?.conversationsByProjectIdAndCreatedAt?.items || [];
  const newNextToken = result.data?.conversationsByProjectIdAndCreatedAt?.nextToken;

  return {
    conversations: conversations,
    nextToken: newNextToken || null,
  };
}

// New helper function that recursively fetches conversations with calls (excludes those without calls)
async function fetchConversationsWithCalls({
  projectId,
  limit = 25,
  nextToken = null,
  accumulator = [] as any[],
}: {
  projectId: string;
  limit?: number;
  nextToken?: string | null;
  accumulator?: any[];
}): Promise<ConversationsPage> {
  const page = await fetchConversations({
    projectId,
    limit,
    nextToken,
  });
  
  // Filter out conversations without calls (assuming calls property exists and is an array)
  const filtered = page.conversations.filter(conv => conv.calls?.items?.length > 0);
  const accumulated = accumulator.concat(filtered);
  
  // If we haven't reached the target limit and there is a next page, recursively fetch more
  if (accumulated.length < limit && page.nextToken) {
    return await fetchConversationsWithCalls({
      projectId,
      limit,
      nextToken: page.nextToken,
      accumulator: accumulated,
    });
  }
  
  // Return the first "limit" conversations with calls and the latest nextToken (if available)
  return {
    conversations: accumulated.slice(0, limit),
    nextToken: page.nextToken,
  };
}

export const useConversations = (projectId: string, limit = 25) => {
  return useInfiniteQuery<ConversationsPage, Error>({
    queryKey: ["conversations", projectId],
    queryFn: async ({ pageParam = null }) => 
      await fetchConversationsWithCalls({
        projectId,
        limit,
        nextToken: pageParam as string | null,
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage: ConversationsPage) =>
      lastPage.nextToken || undefined,
  });
};